import { parseDelimitedText } from "./parseDelimitedText";

export interface TemplateCsvItem {
  readonly category: string;
  readonly timeEstimate?: string;
  readonly itemName: string;
  readonly question?: string;
  readonly note?: string;
  readonly isRequired: boolean;
  readonly order: number;
}

export type TemplateCsvSkippedRowReason =
  | "columns_missing"
  | "item_name_and_question_empty";

export interface TemplateCsvSkippedRow {
  /** 貼り付けテキスト内の物理行番号（1始まり）。セル内改行も行数として数える */
  readonly lineNumber: number;
  readonly content: string;
  readonly reason: TemplateCsvSkippedRowReason;
}

export interface TemplateCsvParseResult {
  readonly items: readonly TemplateCsvItem[];
  readonly skippedRows: readonly TemplateCsvSkippedRow[];
  /** 閉じられていないクォートを検出した場合 true。以降のテキストが1セルに畳み込まれている可能性がある */
  readonly hasUnterminatedQuote: boolean;
}

export function parseTemplateCsv(csvText: string): TemplateCsvParseResult {
  const { records, delimiter, hasUnterminatedQuote } =
    parseDelimitedText(csvText);

  const items: TemplateCsvItem[] = [];
  const skippedRows: TemplateCsvSkippedRow[] = [];
  let isHeaderSkipped = false;

  for (const record of records) {
    const columns = record.columns.map((column) => column.trim());
    if (columns.every((column) => !column)) continue;

    if (!isHeaderSkipped) {
      isHeaderSkipped = true;
      continue;
    }

    if (columns.length < 3) {
      skippedRows.push({
        lineNumber: record.lineNumber,
        content: columns.join(delimiter),
        reason: "columns_missing",
      });
      continue;
    }

    const itemName = columns[2] || "";
    const question = columns[3] || "";
    // 項目名なし・質問のみの行は台本指示行として取り込む。両方空なら見出し等のノイズ行とみなす
    if (!itemName && !question) {
      skippedRows.push({
        lineNumber: record.lineNumber,
        content: columns.join(delimiter),
        reason: "item_name_and_question_empty",
      });
      continue;
    }

    items.push({
      category: columns[0] || "",
      timeEstimate: columns[1] || undefined,
      itemName,
      question: question || undefined,
      note: columns[4] || undefined,
      isRequired: itemName.includes("⭐"),
      order: items.length,
    });
  }

  return { items, skippedRows, hasUnterminatedQuote };
}
